import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QrCode } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';
import { createQRGenerator, composeCanvasWithFrame } from '../../utils/qr';
import './LivePreview.css';

export const LivePreview: React.FC = () => {
  const { t } = useTranslation('create');
  const content = useAppStore(state => state.content);
  const config = useAppStore(state => state.config);
  const logoFile = useAppStore(state => state.logoFile);
  const payloadType = useAppStore(state => state.payloadType);

  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  const [prevContent, setPrevContent] = useState(content);
  if (content !== prevContent) {
    setPrevContent(content);
    if (!content) setError(null);
  }

  useEffect(() => {
    if (!content) {
      if (containerRef.current) containerRef.current.innerHTML = '';
      return;
    }

    let isMounted = true;

    const render = () => {
      try {
        const qr = createQRGenerator(400, content, config, logoFile, true);
        void composeCanvasWithFrame(qr, 400, config).then(compositeCanvas => {
        
          if (isMounted) {
            setError(null);
            if (containerRef.current) {
              containerRef.current.innerHTML = '';
              
              const altText = `QR code: ${t(`types.${payloadType}`)} — ${content.substring(0, 80)}`;
              compositeCanvas.setAttribute('role', 'img');
              compositeCanvas.setAttribute('aria-label', altText);
              
              containerRef.current.appendChild(compositeCanvas);
            }
          }
        });
      } catch {
        if (isMounted) {
          setError(t('create.preview.errorTooLong', 'This content is too long to encode as a QR code at the current error correction level. Try reducing the content length or lowering the error correction level.'));
        }
      }
    };

    render();

    return () => { isMounted = false; };
  }, [content, config, logoFile, payloadType, t]);

  const isEmpty = !content && !error;

  return (
    <div className={`live-preview-wrapper ${isEmpty ? 'empty' : ''} ${error ? 'has-error' : ''}`}>
      {error && <div className="preview-error">{error}</div>}
      
      {isEmpty && (
        <div className="preview-empty">
          <QrCode size={48} className="preview-empty-icon" strokeWidth={1} />
          <span className="preview-empty-text">
            {t('create.preview.empty', 'Type or paste content on the left to generate your QR code')}
          </span>
        </div>
      )}

      <div 
        className="live-preview-container" 
        ref={containerRef}
        style={{ display: (!content || error) ? 'none' : 'block' }}
      />
    </div>
  );
};
